import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Download, MoreHorizontal, FileDown } from 'lucide-react';
import { usePayslips } from '@/composables/payroll/usePayslips';
import { useExport } from '@/composables/shared/useExport';
import { useDebounce } from '@/composables/shared/useDebounce';
import { payslipService } from '@/services/payroll.service';
import { PageContainer } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/data-table/DataTable';
import { DataTableToolbar } from '@/components/data-table/DataTableToolbar';
import { DataTableColumnHeader } from '@/components/data-table/DataTableColumnHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatCurrency, formatDateUTC } from '@/lib/formatters';
import { MONTH_OPTIONS, PAYSLIP_STATUS_OPTIONS, type Payslip } from '@/types/payroll';

export const PAYSLIP_STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-slate-500/15 text-slate-600 dark:text-slate-400',
  PUBLISHED: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
  PAID: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  CANCELLED: 'bg-red-500/15 text-red-600 dark:text-red-400',
  // Muted, not alarming — a superseded payslip is history rather than a problem.
  SUPERSEDED: 'bg-muted text-muted-foreground',
};

/** A short window of years around now — payroll is rarely run far out of range. */
const YEAR_OPTIONS = (() => {
  const now = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, i) => String(now + 1 - i));
})();

export function PayslipListPage() {
  const navigate = useNavigate();
  const { payslips, meta, isLoading, filters, publish, markPaid, cancel, remove, exportCsv } = usePayslips();
  const { exportFile, isExporting } = useExport();
  const [search, setSearch] = useState(filters.current.search);
  const debouncedSearch = useDebounce(search, 300);
  if (debouncedSearch !== filters.current.search) filters.set({ search: debouncedSearch });

  const [deleteTarget, setDeleteTarget] = useState<Payslip | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Payslip | null>(null);

  const downloadPdf = (p: Payslip) =>
    exportFile(() => payslipService.downloadPdf(p.id), `payslip-${p.payslipNumber}.pdf`);

  const columns: ColumnDef<Payslip, unknown>[] = useMemo(() => [
    {
      accessorKey: 'payslipNumber',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Payslip #" />,
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.payslipNumber}</span>,
    },
    {
      accessorKey: 'employeeName',
      header: 'Employee',
      cell: ({ row }) => (
        <div>
          <p className="font-medium">{row.original.employeeName}</p>
          <p className="text-xs text-muted-foreground">{row.original.employeeCode}</p>
        </div>
      ),
    },
    {
      id: 'period',
      header: 'Period',
      cell: ({ row }) => (
        <span>{MONTH_OPTIONS[row.original.periodMonth - 1]?.label} {row.original.periodYear}</span>
      ),
    },
    {
      id: 'attendance',
      header: 'Paid days',
      cell: ({ row }) => {
        const lop = Number(row.original.lopDays);
        return (
          <span className="tabular-nums">
            {Number(row.original.paidDays)}/{row.original.totalDays}
            {lop > 0 && <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">−{lop} LOP</span>}
          </span>
        );
      },
    },
    {
      accessorKey: 'grossEarnings',
      header: 'Gross',
      cell: ({ row }) => <span className="tabular-nums">{formatCurrency(row.original.grossEarnings, row.original.currency)}</span>,
    },
    {
      accessorKey: 'netPay',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Net pay" />,
      cell: ({ row }) => <span className="font-medium tabular-nums">{formatCurrency(row.original.netPay, row.original.currency)}</span>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant="outline" className={`border-0 font-medium ${PAYSLIP_STATUS_STYLES[row.original.status] ?? ''}`}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: 'payDate',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Pay date" />,
      cell: ({ row }) => formatDateUTC(row.original.payDate),
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const p = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/people/payslips/${p.id}`); }}>View</DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); downloadPdf(p); }}>Download PDF</DropdownMenuItem>
              {p.status === 'DRAFT' && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); publish.mutate(p.id); }}>Publish</DropdownMenuItem>
              )}
              {p.status === 'PUBLISHED' && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); markPaid.mutate(p.id); }}>Mark as paid</DropdownMenuItem>
              )}
              {(p.status === 'PUBLISHED' || p.status === 'PAID') && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setCancelTarget(p); }}>Cancel</DropdownMenuItem>
              )}
              {p.status === 'DRAFT' && (
                <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }}>Delete</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PageContainer
      title="Payslips"
      description="Wage slips issued to employees. Once published the figures are frozen — correct one by issuing a replacement, which keeps both on record."
      actions={
        <>
          <Button variant="outline" disabled={isExporting} onClick={() => exportFile(() => exportCsv(), 'payslips.csv')}>
            <Download className="mr-2 h-4 w-4" />Export
          </Button>
          <Button onClick={() => navigate('/people/payslips/new')}>
            <Plus className="mr-2 h-4 w-4" />Generate Payslip
          </Button>
        </>
      }
    >
      <DataTableToolbar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search payslip #, employee…"
        filters={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={filters.current.status || 'all'} onValueChange={(v) => filters.set({ status: v === 'all' ? '' : v, page: 1 })}>
              <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {PAYSLIP_STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.current.periodMonth || 'all'} onValueChange={(v) => filters.set({ periodMonth: v === 'all' ? '' : v, page: 1 })}>
              <SelectTrigger className="w-[140px]"><SelectValue placeholder="Month" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All months</SelectItem>
                {MONTH_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.current.periodYear || 'all'} onValueChange={(v) => filters.set({ periodYear: v === 'all' ? '' : v, page: 1 })}>
              <SelectTrigger className="w-[120px]"><SelectValue placeholder="Year" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {YEAR_OPTIONS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={payslips}
        isLoading={isLoading}
        page={meta?.page ?? 1}
        totalPages={meta?.totalPages ?? 1}
        total={meta?.total ?? 0}
        pageSize={filters.current.limit}
        onPageChange={(p) => filters.set({ page: p })}
        onPageSizeChange={(s) => filters.set({ limit: s })}
        onRowClick={(row) => navigate(`/people/payslips/${row.id}`)}
        emptyMessage="No payslips yet. Generate one to get started."
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Delete Draft Payslip"
        description={`${deleteTarget?.payslipNumber} for ${deleteTarget?.employeeName} will be removed. Only drafts can be deleted.`}
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) { remove.mutate(deleteTarget.id); setDeleteTarget(null); } }}
        isLoading={remove.isPending}
      />

      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={() => setCancelTarget(null)}
        title="Cancel Payslip"
        description={`${cancelTarget?.payslipNumber} will be marked cancelled. It stays on record, and that month becomes free again so a new payslip can be issued for it. To replace an issued payslip while keeping the link between the two, use Correct instead.`}
        variant="destructive"
        confirmLabel="Cancel payslip"
        onConfirm={() => { if (cancelTarget) { cancel.mutate(cancelTarget.id); setCancelTarget(null); } }}
        isLoading={cancel.isPending}
      />
    </PageContainer>
  );
}

/** Shared by the detail page's download button. */
export function PayslipPdfButton({ payslip }: { payslip: Payslip }) {
  const { exportFile, isExporting } = useExport();
  return (
    <Button
      variant="outline"
      disabled={isExporting}
      onClick={() => exportFile(() => payslipService.downloadPdf(payslip.id), `payslip-${payslip.payslipNumber}.pdf`)}
    >
      <FileDown className="mr-2 h-4 w-4" />Download PDF
    </Button>
  );
}
